import { useState } from "react"
import { MoreHorizontal, SquarePen, Trash } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { EditBidDialog } from "@/components/EditBidDialog"
import { DeleteBidDialog } from "@/components/DeleteBidDialog"
import type { Bid } from "@/lib/schemas"

interface BidActionsMenuProps {
  bid: Bid
  onEdited?: () => void
  onDeleted?: () => void
}

/**
 * Secondary bid actions (edit, delete) collapsed into a single overflow menu.
 * Keeps the destructive Delete action one intentional step away from the
 * primary surface rather than sitting as a bare trash icon in the header.
 */
export function BidActionsMenu({ bid, onEdited = () => {}, onDeleted = () => {} }: BidActionsMenuProps) {
  const [showEdit, setShowEdit] = useState(false)
  const [showDelete, setShowDelete] = useState(false)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" title="Bid actions" aria-label="Bid actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem onSelect={() => setShowEdit(true)}>
            <SquarePen className="h-4 w-4 mr-2" />
            Edit bid
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => setShowDelete(true)}
            className="text-muted-foreground focus:text-destructive"
          >
            <Trash className="h-4 w-4 mr-2" />
            Delete bid
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <EditBidDialog bid={bid} open={showEdit} onOpenChange={setShowEdit} onSuccess={onEdited} />
      <DeleteBidDialog bid={bid} open={showDelete} onOpenChange={setShowDelete} onSuccess={onDeleted} />
    </>
  )
}
